import {
  Keypair,
  PublicKey,
  SendOptions,
  Transaction,
  TransactionInstruction,
  VersionedTransaction,
} from "@solana/web3.js";

import {
  buildBackupNotesInstruction,
  buildDepositInstructionsForNotes,
  buildDepositVersionedTransaction,
  buildInitNotesInstructionIfNeeded,
  RelayPayload,
  isExtendBackupPdaNeeded,
  buildRequestHeapIx
} from "../arcaneProgram";
import {
  BACKUPNOTE_TX_BATCH_SIZE,
  DEPOSIT_TX_BATCH_SIZE,
} from "../config/constants";
import { getIndexerUrl, resolveAllowedTokenFromMint } from "../config/index";
import type { Wallet } from "../config/runtime";
import { generateDepositParams, parseSecretNote } from "../zkProof";
import { getConfig } from "../config/runtime";
import { getOracleQuote } from "../rangeSDK";
import {
  buildWithdrawPayload,
  type WithdrawRelayBundle,
  type WithdrawParams,
} from "../withdraw/execute";
import { splitAmountIntoDeposits } from "./splitAmount";

const SEND_OPTIONS: SendOptions = {
  skipPreflight: true,
  maxRetries: 3,
};

export type ArcaneTransferItem = {
  mint: PublicKey;
  /** Human-readable token amount (e.g. `"1.5"` SOL). */
  amount: string;
  recipient: PublicKey;
};

export type ArcaneTransferDepositFailure = {
  amount: string;
  mint: string;
  recipient: string;
};

export type ArcaneTransferWithdrawFailure = {
  note: string;
  recipient: string;
};

export type ArcaneTransferResult = {
  error: boolean;
  depositFailed: ArcaneTransferDepositFailure[];
  withdrawFailed: ArcaneTransferWithdrawFailure[];
  depositSuccess: string[];
};

export type ArcaneTransferOptions = {
  /** When true, all withdraws use the same randomly selected relayer. */
  uniqueRelayer?: boolean;
};

type PreparedDeposit = {
  mint: PublicKey;
  recipient: PublicKey;
  depositAmount: number;
  note: string;
};

type SignableTransaction = Transaction | VersionedTransaction;

type DepositBatch = {
  items: PreparedDeposit[];
  depositTx: VersionedTransaction;
};

function chunk<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

function toDepositFailure(deposit: PreparedDeposit): ArcaneTransferDepositFailure {
  return {
    amount: String(deposit.depositAmount),
    mint: deposit.mint.toBase58(),
    recipient: deposit.recipient.toBase58(),
  };
}

function toWithdrawFailure(
  note: string,
  recipient: PublicKey
): ArcaneTransferWithdrawFailure {
  return {
    note,
    recipient: recipient.toBase58(),
  };
}

function buildTransferResult(
  depositFailed: ArcaneTransferDepositFailure[],
  withdrawFailed: ArcaneTransferWithdrawFailure[],
  successfulDepositTxSignatures: string[]
): ArcaneTransferResult {
  return {
    error: depositFailed.length > 0 || withdrawFailed.length > 0,
    depositFailed,
    withdrawFailed,
    depositSuccess: successfulDepositTxSignatures
  };
}

async function signDepositTransactions(
  wallet: Wallet | Keypair,
  transactions: SignableTransaction[]
): Promise<SignableTransaction[]> {
  if (
    "signAllTransactions" in wallet &&
    typeof wallet.signAllTransactions === "function"
  ) {
    return wallet.signAllTransactions(transactions);
  }

  const keypair =
    wallet instanceof Keypair
      ? wallet
      : wallet.payer instanceof Keypair
        ? wallet.payer
        : null;

  if (!keypair) {
    throw new Error(
      "arcane-privacy-sdk: wallet must provide signAllTransactions (frontend) or be a Keypair / NodeWallet (backend)"
    );
  }

  return transactions.map((tx) => {
    if (tx instanceof VersionedTransaction) {
      tx.sign([keypair]);
    } else {
      tx.partialSign(keypair);
    }
    return tx;
  });
}

function prepareDeposits(items: ArcaneTransferItem[]): PreparedDeposit[] {
  const prepared: PreparedDeposit[] = [];

  for (const item of items) {
    const mintAddress = item.mint.toBase58();
    const { symbol, mint, decimals } = resolveAllowedTokenFromMint(mintAddress);
    const splits = splitAmountIntoDeposits(item.amount, symbol, decimals);
    const noteMint = symbol === "Solana" ? null : mint;

    for (const depositAmount of splits) {
      const amountLamports = BigInt(
        Math.round(depositAmount * 10 ** decimals)
      );
      const { note } = generateDepositParams(amountLamports, noteMint);

      prepared.push({
        mint: item.mint,
        recipient: item.recipient,
        depositAmount,
        note,
      });
    }
  }

  return prepared;
}

async function sendSignedTransaction(
  connection: ReturnType<typeof getConfig>["connection"],
  signed: SignableTransaction
): Promise<{ success: boolean, signature: string }> {
  const signature = await connection.sendRawTransaction(
    signed.serialize(),
    SEND_OPTIONS
  );
  const { value } = await connection.confirmTransaction(signature, "confirmed");

  return {
    success: value.err === null,
    signature,
  };
}

function compactU16Size(value: number): number {
  if (value <= 0x7f) return 1;
  if (value <= 0x3fff) return 2;
  return 3;
}

function estimateCompiledInstructionSize(
  accountCount: number,
  dataLength: number
): number {
  return (
    1 +
    compactU16Size(accountCount) +
    accountCount +
    compactU16Size(dataLength) +
    dataLength
  );
}

function getTransactionSizeBytes(tx: SignableTransaction): number {
  if (tx instanceof VersionedTransaction) {
    return tx.serialize().length;
  }

  return tx.serialize({
    requireAllSignatures: false,
    verifySignatures: false,
  }).length;
}

function resolveV0ProgramId(
  message: VersionedTransaction["message"],
  programIdIndex: number
): string {
  if (programIdIndex < message.staticAccountKeys.length) {
    return message.staticAccountKeys[programIdIndex]!.toBase58();
  }

  return `lookup-account[${programIdIndex - message.staticAccountKeys.length}]`;
}

function logTransactionSizeAnalysis(
  tx: SignableTransaction,
  index: number,
  type: "deposit" | "backup"
): void {
  const maxTxSize = 1232;

  if (tx instanceof VersionedTransaction) {
    const message = tx.message;
    const messageBytes = message.serialize();
    const fullTxBytes = tx.serialize();

    console.log(`\n--- transactionsToSign[${index}] (${type}) ---`);
    console.log(
      `  full tx: ${fullTxBytes.length} bytes (${maxTxSize - fullTxBytes.length} remaining, max ${maxTxSize})`
    );
    console.log(`  message: ${messageBytes.length} bytes`);
    console.log(
      `  signatures: ${message.header.numRequiredSignatures} required, ${message.header.numReadonlySignedAccounts} readonly signed, ${message.header.numReadonlyUnsignedAccounts} readonly unsigned`
    );
    console.log(`  static accounts: ${message.staticAccountKeys.length}`);
    console.log(`  lookup tables: ${message.addressTableLookups.length}`);

    let instructionWireEstimate = 0;
    message.compiledInstructions.forEach((ix, ixIndex) => {
      const accountCount = ix.accountKeyIndexes.length;
      const dataLength = ix.data.length;
      const wireEstimate = estimateCompiledInstructionSize(
        accountCount,
        dataLength
      );
      instructionWireEstimate += wireEstimate;

      console.log(
        `  ix[${ixIndex}]: program=${resolveV0ProgramId(message, ix.programIdIndex)}, accounts=${accountCount}, data=${dataLength}B, wire~${wireEstimate}B`
      );
    });

    console.log(
      `  instructions wire total: ~${instructionWireEstimate}B (non-instruction message overhead: ~${messageBytes.length - instructionWireEstimate}B)`
    );
    return;
  }

  const message = tx.compileMessage();
  const messageBytes = message.serialize();
  const fullTxBytes = tx.serialize({
    requireAllSignatures: false,
    verifySignatures: false,
  });

  console.log(`\n--- transactionsToSign[${index}] (${type}) ---`);
  console.log(
    `  full tx: ${fullTxBytes.length} bytes (${maxTxSize - fullTxBytes.length} remaining, max ${maxTxSize})`
  );
  console.log(`  message: ${messageBytes.length} bytes`);
  console.log(`  accounts: ${message.accountKeys.length}`);

  let instructionWireEstimate = 0;
  tx.instructions.forEach((ix, ixIndex) => {
    const accountCount = ix.keys.length;
    const dataLength = ix.data.length;
    const wireEstimate = estimateCompiledInstructionSize(
      accountCount,
      dataLength
    );
    instructionWireEstimate += wireEstimate;

    console.log(
      `  ix[${ixIndex}]: program=${ix.programId.toBase58()}, accounts=${accountCount}, data=${dataLength}B, wire~${wireEstimate}B`
    );
  });

  console.log(
    `  instructions wire total: ~${instructionWireEstimate}B (non-instruction message overhead: ~${messageBytes.length - instructionWireEstimate}B)`
  );
}

async function submitWithdrawPayloads(
  payloads: RelayPayload[]
): Promise<unknown> {
  const response = await fetch(`${getIndexerUrl()}/submit`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(payloads),
  });
  const result = (await response.json()) as { data?: unknown };
  return result;
}

async function buildBackupTransaction(
  senderPublicKey: PublicKey,
  backupNotesIxs: TransactionInstruction[]
): Promise<Transaction> {
  const { connection } = getConfig();
  const tx = new Transaction();
  backupNotesIxs.forEach((ix) => tx.add(ix));

  const { blockhash } = await connection.getLatestBlockhash();
  tx.recentBlockhash = blockhash;
  tx.feePayer = senderPublicKey;

  return tx;
}

export async function executeArcaneTransfer(
  items: ArcaneTransferItem[],
): Promise<ArcaneTransferResult> {
  const depositFailed: ArcaneTransferDepositFailure[] = [];
  const withdrawFailed: ArcaneTransferWithdrawFailure[] = [];

  if (!items.length) {
    return buildTransferResult(depositFailed, withdrawFailed, []);
  }

  const { wallet, connection, relayers } = getConfig();
  const senderPublicKey = wallet.publicKey;

  const prepared = prepareDeposits(items);
  const allNotes = prepared.map((item) => item.note);

  let builtDepositBatches: DepositBatch[] = [];
  let backupTxs: Transaction[] = [];
  const withdrawPayloadResults = await Promise.all(
    prepared.map((deposit) =>
      buildWithdrawPayload({
        note: deposit.note,
        recipient: deposit.recipient,
        arcaneTransferMode: true,
      } satisfies WithdrawParams, allNotes)
    )
  );

  const withdrawPayloads: RelayPayload[] = [];
  withdrawPayloadResults.forEach((result, index) => {
    const deposit = prepared[index]!;
      withdrawPayloads.push(result.payload);
    }
  );

  try {
    const extendBackupPdaNeeded = await isExtendBackupPdaNeeded(senderPublicKey, allNotes.length)

    const [{ sigVerifyIx }, depositInstructions, initNotesIx] =
      await Promise.all([
        getOracleQuote(senderPublicKey, senderPublicKey.toBase58()),
        buildDepositInstructionsForNotes(senderPublicKey, allNotes),
        buildInitNotesInstructionIfNeeded(senderPublicKey),
      ]);

    const indexedDeposits = prepared.map((item, index) => ({
      item,
      depositIx: depositInstructions[index]!,
    }));

    const depositChunks = chunk(indexedDeposits, DEPOSIT_TX_BATCH_SIZE);
    const noteChunks = chunk(allNotes, BACKUPNOTE_TX_BATCH_SIZE);

    [builtDepositBatches, backupTxs] = await Promise.all([
      Promise.all(
        depositChunks.map(async (batch, batchIndex) => {
          const instructions: TransactionInstruction[] = [
            sigVerifyIx,
            ...batch.map((entry) => entry.depositIx),
          ];

          if (batchIndex === 0 && initNotesIx) {
            instructions.push(initNotesIx);
          }
          instructions.map((ix) => {
            const keys: string[] = []
            ix.keys.map((key) => keys.push(key.pubkey.toBase58()))
          })

          const depositTx = await buildDepositVersionedTransaction(
            senderPublicKey,
            instructions
          );
          // console.log(
          //   `deposit batch[${batchIndex}] tx size:`,
          //   getTransactionSizeBytes(depositTx),
          //   "bytes"
          // );

          return {
            items: batch.map((entry) => entry.item),
            depositTx,
          };
        })
      ),
      Promise.all(
        noteChunks.map(async (noteBatch, batchIndex) => {
          const backupNotesIx = await buildBackupNotesInstruction(
            senderPublicKey,
            noteBatch
          );
          let requestHeapIx: TransactionInstruction[] = []
          if (extendBackupPdaNeeded && batchIndex === 0) {
            requestHeapIx.push(buildRequestHeapIx())
          }

          const backupTx = await buildBackupTransaction(
            senderPublicKey,
            [...requestHeapIx, backupNotesIx]
          );
          // console.log(
          //   `backup batch[${batchIndex}] tx size:`,
          //   getTransactionSizeBytes(backupTx),
          //   "bytes"
          // );
          return backupTx;
        })
      ),
    ]);
  } catch {
    prepared.forEach((item) => depositFailed.push(toDepositFailure(item)));
  }

  if (!builtDepositBatches.length) {
    return buildTransferResult(depositFailed, withdrawFailed, []);
  }

  const transactionsToSign = [
    ...builtDepositBatches.map((batch) => batch.depositTx),
    ...backupTxs,
  ];

  transactionsToSign.forEach((tx, index) => {
    // const type =
    //   index < builtDepositBatches.length ? "deposit" : "backup";
    // logTransactionSizeAnalysis(tx, index, type);
  });

  const signedTransactions = await signDepositTransactions(
    wallet,
    transactionsToSign
  );

  const depositTxCount = builtDepositBatches.length;
  const [sendOutcomes, withdrawSubmitOutcome] = await Promise.all([
    Promise.allSettled(
      signedTransactions.map((signed) =>
        sendSignedTransaction(connection, signed)
      )
    ),
    withdrawPayloads.length > 0
      ? submitWithdrawPayloads(withdrawPayloads).then(
          (value) => ({ status: "fulfilled" as const, value }),
          (reason) => ({ status: "rejected" as const, reason })
        )
      : Promise.resolve(undefined),
  ]);

  const successfulDeposits: PreparedDeposit[] = [];
  const successfulDepositTxSignatures: string[] = [];

  sendOutcomes.slice(0, depositTxCount).forEach((outcome, index) => {
    const batch = builtDepositBatches[index]!;
    const depositSucceeded =
      outcome.status === "fulfilled" && outcome.value.success;

    if (depositSucceeded) {
      successfulDeposits.push(...batch.items);
      successfulDepositTxSignatures.push(outcome.value.signature);
      return;
    }

    batch.items.forEach((item) => depositFailed.push(toDepositFailure(item)));
  });

  if (!successfulDeposits.length) {
    return buildTransferResult(depositFailed, withdrawFailed, []);
  }

  if (
    withdrawSubmitOutcome?.status === "rejected"
  ) {
    
  } else if (withdrawSubmitOutcome?.status === "fulfilled") {
    if (!(withdrawSubmitOutcome?.value as any).success) {
      const failedNullifierHashes: string[] = [];
      (withdrawSubmitOutcome?.value as any).items.map((item: any) => {
        if (item.status == 'failed') {
          failedNullifierHashes.push(item.nullifierHash)
        }
      })

      successfulDeposits.forEach((item, index) => {
        const {nullifierHash} = parseSecretNote(item.note)
        if (nullifierHash && failedNullifierHashes.includes(nullifierHash)) {
          withdrawFailed.push(toWithdrawFailure(item.note, item.recipient));
        }
      })
    } 

  }

  return buildTransferResult(depositFailed, withdrawFailed, successfulDepositTxSignatures);
}
